/**
 * Produkte des Haushalts: Liste mit Suche und ein Editor für Angaben, Bild
 * und Packungen (Hersteller, Inhalt, EAN, Preis). Produkte verbinden gleiche
 * Zutaten auf der Einkaufsliste und schlagen Packungen und Preise vor.
 */
import {
  NUTRIENT_FIELDS,
  SHOPPING_CATEGORIES,
  categoryLabel,
  formatDecimal,
  genericNutrients,
} from '@cookbook/domain';
import type { Nutrients } from '@cookbook/domain';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.ts';
import type { Product, ProductVariant } from '../api/types.ts';
import { useQuery } from '../api/useQuery.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { BaseStockDialog } from '../components/products/BaseStockDialog.tsx';
import { Provenance } from '../components/products/Provenance.tsx';
import { FoodDetails } from '../components/products/SourceNote.tsx';
import { ScanDialog } from '../components/products/ScanDialog.tsx';
import { SourceSearch } from '../components/products/SourceSearch.tsx';
import { SupplementPanel } from '../components/products/SupplementPanel.tsx';
import { formatPrice } from '../lib/shoppingFormat.ts';
import { texts } from '../texts/de.ts';

/** Liest einen Preis wie „0,99“ in Cent. */
function parsePrice(text: string): number | null {
  const value = Number(text.replace('€', '').trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null;
}

/** Feld mit Beschriftung. */
function Field({
  label,
  children,
  wide = false,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly wide?: boolean;
}) {
  return (
    <label className={`block text-sm ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="mb-1 block text-xs text-content-muted">{label}</span>
      {children}
    </label>
  );
}

/** Nährwerte als Texte für die Eingabefelder; leer ohne eigene Werte. */
function nutritionTexts(values: Nutrients | null): Record<keyof Nutrients, string> {
  return Object.fromEntries(
    NUTRIENT_FIELDS.map((field) => [field.key, values === null ? '' : formatDecimal(values[field.key])]),
  ) as Record<keyof Nutrients, string>;
}

/** Eingabefeld im einheitlichen Stil. */
const inputClass = 'w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5';

/** Bildauswahl mit Vorschau. */
function ImagePicker({
  thumbUrl,
  onUpload,
  onRemove,
}: {
  readonly thumbUrl: string | null;
  readonly onUpload: (file: File) => void;
  readonly onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      {thumbUrl === null ? (
        <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-surface-sunken text-content-muted">
          <AppIcon name="tag" size={24} />
        </span>
      ) : (
        <img src={thumbUrl} alt="" className="h-16 w-16 rounded-xl object-cover" />
      )}
      <label className="cursor-pointer rounded-lg border border-border-subtle px-3 py-1.5 text-sm">
        {texts.products.uploadImage}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) {
              onUpload(file);
            }
            event.target.value = '';
          }}
        />
      </label>
      {thumbUrl === null || onRemove === undefined ? null : (
        <button type="button" onClick={onRemove} className="text-sm text-content-muted underline">
          {texts.products.removeImage}
        </button>
      )}
    </div>
  );
}

/** Bearbeitung einer Packung. */
function VariantForm({
  variant,
  onSave,
  onDelete,
  onImage,
}: {
  readonly variant: ProductVariant | null;
  readonly onSave: (values: Record<string, unknown>) => void;
  readonly onDelete?: () => void;
  readonly onImage?: (file: File) => void;
}) {
  const [brand, setBrand] = useState(variant?.brand ?? '');
  const [label, setLabel] = useState(variant?.label ?? '');
  const [amount, setAmount] = useState(
    variant?.packageAmount === null || variant === null ? '' : formatDecimal(variant.packageAmount),
  );
  const [unit, setUnit] = useState(variant?.packageUnit ?? 'g');
  const [ean, setEan] = useState(variant?.ean ?? '');
  const [price, setPrice] = useState(
    variant?.priceCents === null || variant === null ? '' : formatDecimal(variant.priceCents / 100),
  );
  const [preferred, setPreferred] = useState(variant?.preferred ?? false);

  return (
    <form
      className="grid gap-2 rounded-xl border border-border-subtle p-3 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          brand,
          label,
          packageAmount: amount.replace(',', '.'),
          packageUnit: unit,
          ean,
          priceCents: parsePrice(price),
          preferred,
        });
        if (variant === null) {
          setBrand('');
          setLabel('');
          setAmount('');
          setEan('');
          setPrice('');
        }
      }}
    >
      {onImage === undefined ? null : (
        <div className="sm:col-span-2">
          <ImagePicker thumbUrl={variant?.thumbUrl ?? null} onUpload={onImage} />
        </div>
      )}
      {variant === null ? null : (
        <div className="space-y-1 sm:col-span-2">
          <FoodDetails allergens={variant.allergens} labels={variant.labels} />
          <Provenance entries={variant.provenance} />
        </div>
      )}
      <Field label={texts.products.brand}>
        <input value={brand} onChange={(event) => setBrand(event.target.value)} className={inputClass} />
      </Field>
      <Field label={texts.products.label}>
        <input value={label} onChange={(event) => setLabel(event.target.value)} className={inputClass} />
      </Field>
      <div className="flex gap-2">
        <Field label={texts.products.packageAmount}>
          <input
            value={amount}
            inputMode="decimal"
            onChange={(event) => setAmount(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={texts.products.packageUnit}>
          <input value={unit} onChange={(event) => setUnit(event.target.value)} className={inputClass} />
        </Field>
      </div>
      <Field label={texts.products.price}>
        <input
          value={price}
          inputMode="decimal"
          onChange={(event) => setPrice(event.target.value)}
          className={inputClass}
        />
      </Field>
      <Field label={texts.products.ean}>
        <input
          value={ean}
          inputMode="numeric"
          onChange={(event) => setEan(event.target.value)}
          className={inputClass}
        />
      </Field>
      <label className="flex items-center gap-2 self-end text-sm">
        <input type="checkbox" checked={preferred} onChange={(event) => setPreferred(event.target.checked)} />
        {texts.products.preferred}
      </label>
      <div className="flex gap-2 sm:col-span-2">
        <button
          type="submit"
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-on-accent"
        >
          {variant === null ? texts.products.addVariant : texts.common.save}
        </button>
        {onDelete === undefined ? null : (
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-content-muted"
          >
            {texts.products.delete}
          </button>
        )}
      </div>
    </form>
  );
}

/** Editor eines Produkts. */
function ProductEditor({ id }: { readonly id: string }) {
  const navigate = useNavigate();
  const { data, loading, error, setData } = useQuery<{ product: Product }>(`/api/products/${id}`);
  const product = data?.product ?? null;
  const [name, setName] = useState('');
  const [category, setCategory] = useState('other');
  const [defaultUnit, setDefaultUnit] = useState('');
  const [synonyms, setSynonyms] = useState('');
  const [note, setNote] = useState('');
  const [nutrition, setNutrition] = useState(() => nutritionTexts(null));
  const [pieceGrams, setPieceGrams] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  // Steigt nach dem Ergänzen aus einer Quelle, damit das Formular die neuen Werte zeigt.
  const [formVersion, setFormVersion] = useState(0);

  useEffect(() => {
    if (product !== null) {
      setName(product.name);
      setCategory(product.category);
      setDefaultUnit(product.defaultUnit);
      setSynonyms(product.synonyms.join(', '));
      setNote(product.note ?? '');
      setNutrition(nutritionTexts(product.nutrition));
      setPieceGrams(product.pieceGrams === null ? '' : formatDecimal(product.pieceGrams));
    }
    // Nur beim Laden eines anderen Produkts übernehmen, nicht nach jedem Speichern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, formVersion]);

  /** Führt eine Änderung aus und übernimmt das Ergebnis. */
  async function run(work: () => Promise<{ product: Product }>, done?: string) {
    setMessage(null);
    try {
      setData(await work());
      if (done !== undefined) {
        setMessage(done);
      }
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  if (loading) {
    return <p className="text-sm text-content-muted">{texts.common.loading}</p>;
  }
  if (product === null) {
    return <p className="text-sm text-accent-strong">{error ?? texts.common.error}</p>;
  }
  const guide = genericNutrients(name);

  return (
    <div className="space-y-4">
      <Link to="/produkte" className="inline-flex items-center gap-1 text-sm text-content-muted">
        ← {texts.products.back}
      </Link>
      <form
        className="grid gap-3 rounded-card border border-border-subtle bg-surface-raised p-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          void run(
            () =>
              api.put(`/api/products/${id}`, {
                name,
                category,
                defaultUnit,
                synonyms: synonyms.split(',').map((entry) => entry.trim()),
                note,
                // Nur ausgefüllte Felder; ganz leer gelten wieder die Richtwerte.
                nutrition: Object.fromEntries(
                  Object.entries(nutrition).filter(([, value]) => value.trim() !== ''),
                ),
                pieceGrams: pieceGrams.replace(',', '.'),
              }),
            texts.products.saved,
          );
        }}
      >
        <div className="sm:col-span-2">
          <ImagePicker
            thumbUrl={product.thumbUrl}
            onUpload={(file) => void run(() => api.upload(`/api/products/${id}/image`, file))}
            onRemove={() => void run(() => api.delete(`/api/products/${id}/image`))}
          />
        </div>
        <Field label={texts.products.name}>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={inputClass}
            required
          />
        </Field>
        <Field label={texts.products.category}>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className={inputClass}
          >
            {SHOPPING_CATEGORIES.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={texts.products.defaultUnit}>
          <input
            value={defaultUnit}
            onChange={(event) => setDefaultUnit(event.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label={texts.products.synonyms}>
          <input
            value={synonyms}
            onChange={(event) => setSynonyms(event.target.value)}
            placeholder={texts.products.synonymsHint}
            className={inputClass}
          />
        </Field>
        <Field label={texts.products.note} wide>
          <input value={note} onChange={(event) => setNote(event.target.value)} className={inputClass} />
        </Field>
        <fieldset className="sm:col-span-2">
          <legend className="mb-1 text-sm font-semibold">{texts.products.nutrition}</legend>
          <p className="mb-2 text-xs text-content-muted">{texts.products.nutritionHint}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {NUTRIENT_FIELDS.map((field) => (
              <Field key={field.key} label={`${texts.nutrition.fields[field.key]} (${field.unit})`}>
                <input
                  value={nutrition[field.key]}
                  inputMode="decimal"
                  placeholder={guide === null ? '' : formatDecimal(guide[field.key])}
                  onChange={(event) => setNutrition({ ...nutrition, [field.key]: event.target.value })}
                  className={inputClass}
                />
              </Field>
            ))}
            <Field label={texts.products.pieceGrams}>
              <input
                value={pieceGrams}
                inputMode="decimal"
                onChange={(event) => setPieceGrams(event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <button type="submit" className="rounded-lg bg-accent px-4 py-2 font-semibold text-on-accent">
            {texts.common.save}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(texts.products.deleteConfirm)) {
                void api.delete(`/api/products/${id}`).then(() => navigate('/produkte'));
              }
            }}
            className="rounded-lg border border-border-subtle px-4 py-2 text-content-muted"
          >
            {texts.products.delete}
          </button>
        </div>
        {message === null ? null : <p className="text-sm text-content-muted sm:col-span-2">{message}</p>}
        <div className="sm:col-span-2">
          <Provenance entries={product.provenance} />
        </div>
      </form>

      <SupplementPanel
        product={product}
        onApplied={(updated) => {
          setData({ product: updated });
          setFormVersion((version) => version + 1);
        }}
      />

      <section className="space-y-3">
        <h2 className="font-semibold">{texts.products.variants}</h2>
        {product.variants.map((variant) => (
          <VariantForm
            key={`${variant.id}-${variant.preferred}-${formVersion}`}
            variant={variant}
            onSave={(values) =>
              void run(() => api.put(`/api/variants/${variant.id}`, values), texts.products.saved)
            }
            onDelete={() => void run(() => api.delete(`/api/variants/${variant.id}`))}
            onImage={(file) => void run(() => api.upload(`/api/variants/${variant.id}/image`, file))}
          />
        ))}
        <VariantForm
          variant={null}
          onSave={(values) => void run(() => api.post(`/api/products/${id}/variants`, values))}
        />
      </section>
    </div>
  );
}

/** Kurzbeschreibung der bevorzugten Packung. */
function variantSummary(product: Product): string {
  const variant = product.variants.find((entry) => entry.preferred) ?? product.variants[0];
  if (variant === undefined) {
    return '';
  }
  const size =
    variant.packageAmount === null ? '' : `${formatDecimal(variant.packageAmount)} ${variant.packageUnit}`;
  const price = variant.priceCents === null ? '' : formatPrice(variant.priceCents);
  return [variant.brand, size, price].filter((part) => part !== null && part !== '').join(' · ');
}

/** Liste der Produkte mit Suche und Neuanlage. */
function ProductList() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { data, loading, error, reload } = useQuery<{ products: Product[] }>(
    `/api/products?q=${encodeURIComponent(query)}`,
  );
  const [name, setName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setScanning(true)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 text-sm"
        >
          <AppIcon name="barcode" size={18} />
          {texts.scan.title}
        </button>
        <button
          type="button"
          onClick={() => setSearching(!searching)}
          aria-expanded={searching}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 text-sm"
        >
          <AppIcon name="search" size={18} />
          {texts.scan.searchSources}
        </button>
        <button
          type="button"
          onClick={() => setImporting(true)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 text-sm"
        >
          <AppIcon name="cart" size={18} />
          {texts.baseStock.button}
        </button>
      </div>
      {importing ? <BaseStockDialog onClose={() => setImporting(false)} onImported={reload} /> : null}
      {scanning ? <ScanDialog onClose={() => setScanning(false)} /> : null}
      {searching ? (
        <div className="rounded-card border border-border-subtle bg-surface-raised p-4">
          <SourceSearch onAdopted={(product) => void navigate(`/produkte/${product.id}`)} />
        </div>
      ) : null}
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setMessage(null);
          api
            .post<{ product: Product }>('/api/products', { name })
            .then((result) => navigate(`/produkte/${result.product.id}`))
            .catch((caught: unknown) =>
              setMessage(caught instanceof ApiError ? caught.message : texts.common.error),
            );
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={texts.products.name}
          aria-label={texts.products.newProduct}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
        />
        <button
          type="submit"
          disabled={name.trim() === ''}
          className="rounded-xl bg-accent px-4 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.products.newProduct}
        </button>
      </form>
      {message === null ? null : <p className="text-sm text-accent-strong">{message}</p>}
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={texts.products.search}
        aria-label={texts.products.search}
        className="w-full rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
      />
      {loading && data === null ? <p className="text-sm text-content-muted">{texts.common.loading}</p> : null}
      {error === null ? null : <p className="text-sm text-accent-strong">{error}</p>}
      {data !== null && data.products.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {texts.products.empty}
        </p>
      ) : null}
      <ul className="divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-raised">
        {(data?.products ?? []).map((product) => (
          <li key={product.id}>
            <Link
              to={`/produkte/${product.id}`}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-sunken"
            >
              {product.thumbUrl === null ? (
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-surface-sunken text-content-muted">
                  <AppIcon name="tag" size={18} />
                </span>
              ) : (
                <img src={product.thumbUrl} alt="" className="h-11 w-11 rounded-lg object-cover" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{product.name}</span>
                <span className="block truncate text-xs text-content-muted">
                  {[categoryLabel(product.category), variantSummary(product)]
                    .filter((part) => part !== '')
                    .join(' · ')}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Seite der Produkte. */
export function ProductsPage() {
  const { id } = useParams();
  return (
    <section className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight">{texts.products.title}</h1>
        <p className="mt-0.5 text-sm text-content-muted">{texts.products.subtitle}</p>
      </header>
      {id === undefined ? <ProductList /> : <ProductEditor key={id} id={id} />}
    </section>
  );
}
